import { t } from "i18next";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import Icons from "@app/assets/Icons";
import InactiveSubmission from "@app/components/InactiveSubmission";
import Loader from "@app/components/Loader";
import Pagination from "@app/components/Pagination";
import TableHeader from "@app/components/TableHeader";
import ViewUtilityHeader from "@app/components/ViewUtilityHeader";
import { RouteNames } from "@app/routes/index";
import { getItemsData } from "@app/services/Endpoints/Item.API";
import { HeadersMapInterface } from "@app/types/Common.types";
import { Item } from "@app/types/Item";
import { AppContext } from "@app/utils/contexts/App.context";
import { FilterContext } from "@app/utils/contexts/Filters.context";

import "./index.scss";

let totalPages = 0;
const sortByEnum = {
    DESC: 1,
    ASC: 0,
};

const ViewItemPage: FC = () => {
    const { userPermissions } = useContext(AppContext);
    const { statusesFilter, stationsFilter } = useContext(FilterContext);
    const navigate = useNavigate();
    const { state } = useLocation();
    const [items, setItems] = useState<Item[]>([]);
    const [sortByObj, setSortByObj] = useState({ sortBy: "name", sortOrder: sortByEnum.DESC });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [loader, setLoader] = useState<boolean>(false);
    const [searchVal, setSearchVal] = useState<string>("");
    const [isScrollComing, setIsScrollComing] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ref = useRef<any>();
    const HeadersMap: HeadersMapInterface = {
        serialNo: {
            label: t("VIEW.ITEM.SERIALNO"),
            layout: { lg: 1, sm: 1 },
            disableSort: true,
        },
        name: { label: t("VIEW.ITEM.NAME"), layout: { lg: 2, sm: 2 } },
        displayName: { label: t("VIEW.ITEM.DISPLAYNAME"), layout: { lg: 2, sm: 2 } },
        id: {
            label: t("VIEW.ITEM.ID"),
            layout: { lg: 2, sm: 2 },
        },
        cookingStationName: { label: t("VIEW.ITEM.STATION"), layout: { lg: 2, sm: 2 } },
        status: { label: t("VIEW.ITEM.STATUS"), layout: { lg: 2, sm: 2 } },
    };

    const machineId = state?.machineId;
    const machineName = state?.machineName;

    useEffect(() => {
        setPageNumber(1);
    }, [statusesFilter, stationsFilter, machineId]);

    useEffect(() => {
        const filters = [
            statusesFilter?.map((status) => `&Status=${status}`).join(""),
            stationsFilter?.map((stationId) => `&StationIds=${stationId}`).join(""),
            machineId && `MachineConfigId=${machineId}`,
        ].join("&");
        getItems(sortByObj.sortBy, sortByObj.sortOrder, pageNumber, pageSize, searchVal, filters);
    }, [pageNumber, pageSize, sortByObj, searchVal, statusesFilter, stationsFilter, machineId]);

    useEffect(() => {
        if (ref.current?.offsetWidth != ref.current?.clientWidth) {
            setIsScrollComing(true);
        } else {
            setIsScrollComing(false);
        }
    });

    const getItems = (
        sortBy: string,
        sortOrder: number,
        pageNumber: number,
        pageSize: number,
        name?: string,
        filters?: string,
    ) => {
        setLoader(true);
        getItemsData(sortBy, sortOrder, pageNumber, pageSize, name, filters)
            .then((res) => {
                totalPages = res.data.totalPages;
                const result = res?.data?.data;
                if (result) {
                    setItems([...result]);
                } else {
                    setItems([]);
                }
            })
            .catch((err) => console.error(err))
            .finally(() => setLoader(false));
    };

    const sortByHandler = (headerKey: string) => {
        setSortByObj({ sortBy: headerKey, sortOrder: Number(!sortByObj.sortOrder) });
    };

    const onChangeRecordsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.target;
        setPageSize(Number(value));
        setPageNumber(1);
    };

    const onPageChange = (pageNumber: number) => {
        setPageNumber(Number(pageNumber));
    };
    const onEdit = (itemData: Item) => {
        navigate(RouteNames.Edit, {
            state: { itemEditData: itemData },
        });
    };
    const redirectToAddCustomisation = () => {
        navigate(RouteNames.Add);
    };

    const handleSearch = (val: string) => {
        setSearchVal(val);
    };
    return (
        <Container fluid className='items'>
            <ViewUtilityHeader
                onChange={onChangeRecordsPerPage}
                placeholder='Search for Item name'
                btnLabel={t("VIEW.ITEM.ADDITEM")}
                onClick={userPermissions?.Create_Item ? redirectToAddCustomisation : undefined}
                onSearch={handleSearch}
            />

            {machineName && machineId && (
                <InactiveSubmission title='Items for Machine:' subTitle={machineName} />
            )}

            <TableHeader
                headersMap={HeadersMap}
                sortByHandler={sortByHandler}
                sortByObj={sortByObj}
                enableIconColumn
            />

            <div
                className={`items--page ${isScrollComing ? "items--isScroll" : ""}`}
                id='listing-Outercontainer'
                ref={ref}
            >
                {loader ? (
                    <Loader />
                ) : items.length ? (
                    items.map((item, index) => (
                        <Row
                            key={index}
                            className={`items__rowWrap ${index % 2 == 0 && "items__rowGrey"}`}
                        >
                            <Col className='items--pl20' sm='1' lg='1'>
                                {pageNumber == 1 && index < 9 ? "0" : ""}
                                {(pageNumber - 1) * 10 + (index + 1)}
                            </Col>
                            <Col sm='2' lg='2'>
                                {item.name}
                            </Col>
                            <Col sm='2' lg='2'>
                                {item.displayName}
                            </Col>

                            <Col sm='2' lg='2'>
                                {item.code}
                            </Col>

                            <Col sm='2' lg='2'>
                                {item.cookingStationName}
                            </Col>
                            <Col sm='2' lg='2'>
                                {item.status ? t("VIEW.USER.ACTIVE") : t("VIEW.USER.INACTIVE")}
                            </Col>
                            <Col sm='1' lg='1'>
                                {userPermissions?.Edit_Item && (
                                    <img
                                        className='items__enablepointer'
                                        src={Icons.EditIcon}
                                        onClick={() => onEdit(item)}
                                        height='20px'
                                    />
                                )}
                            </Col>
                        </Row>
                    ))
                ) : (
                    <div className='users--noData'>{t("APIMSG.NODATA")}</div>
                )}
            </div>
            <Pagination
                sortByObj={sortByObj}
                onPageChange={onPageChange}
                totalPages={totalPages}
                pageSize={pageSize}
                pageNumber={pageNumber}
            />
        </Container>
    );
};

export default ViewItemPage;
